import {
  Box,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CopyableTypography from "components/Typography/CopyableTypography";
import {
  CohortMemberStatus,
  ContactReportRole,
  GetCohortQuery,
} from "generated-graphql/graphql";
import { useMemo, useState } from "react";
import { CohortStateFields } from "./CohortStateFields";
import { NaicsCodesModal } from "./NaicsCodesModal";
import { StateSummary } from "./StateSummary";
import { SummaryMetric } from "./SummaryMetric";
import { ContactInfo, UniqueContactsModal } from "./UniqueContactsModal";

type CohortData = NonNullable<GetCohortQuery["cohort"]>;

interface CohortClosedSummaryProps {
  cohort: CohortData;
}

interface ContactFieldProps {
  label: string;
  value: string;
}

function ContactField({ label, value }: ContactFieldProps) {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={2} alignItems="baseline">
      <Typography
        variant="caption"
        sx={{
          minWidth: "60px",
          color: theme.palette.text.secondary,
          fontSize: "12px",
          lineHeight: "12px",
        }}
      >
        {label}
      </Typography>
      <CopyableTypography
        text={value || "--"}
        sx={{ fontSize: "14px", lineHeight: "1.5" }}
      />
    </Stack>
  );
}

function ContactCard({ contact }: { contact: ContactInfo }) {
  return (
    <Stack
      direction="column"
      sx={{
        mb: 2,
      }}
    >
      <Stack direction="column" spacing={0.75}>
        <ContactField label="Name" value={contact.name} />
        {contact.title && <ContactField label="Title" value={contact.title} />}
        {contact.email && <ContactField label="Email" value={contact.email} />}
        {contact.phone && <ContactField label="Phone" value={contact.phone} />}
      </Stack>
    </Stack>
  );
}

// How many items to show inline before "View all" link
const MAX_INLINE_NAICS = 3;

export function CohortClosedSummary({ cohort }: CohortClosedSummaryProps) {
  const theme = useTheme();

  // Modal state
  const [verifiersModalOpen, setVerifiersModalOpen] = useState(false);
  const [ownerOperatorsModalOpen, setOwnerOperatorsModalOpen] = useState(false);
  const [naicsModalOpen, setNaicsModalOpen] = useState(false);

  // Calculate summary metrics
  const summaryData = useMemo(() => {
    const members = cohort.members ?? [];

    // Total READY members
    const readyMembers = members.filter(
      (m) => m.status === CohortMemberStatus.Ready
    );
    const readyCount = readyMembers.length;

    // Chemical counts from cohort-level computed field (avoids N+1 queries)
    const totalChemicals = cohort.chemicalCounts?.total ?? 0;
    const ehsChemicals = cohort.chemicalCounts?.ehs ?? 0;

    // Unique verifiers from TierIIReport (READY members only)
    const verifierMap = new Map<string, ContactInfo>();
    for (const member of readyMembers) {
      const report = member.tierIIReport;
      if (report?.verifierFullName) {
        const key = `${report.verifierFullName}-${report.verifierEmail ?? ""}`;
        if (!verifierMap.has(key)) {
          verifierMap.set(key, {
            name: report.verifierFullName,
            email: report.verifierEmail,
            phone: report.verifierPhone,
            title: report.verifierTitle,
          });
        }
      }
    }
    const uniqueVerifiers = Array.from(verifierMap.values());

    // Unique owner/operators from facility contacts (READY members only)
    const ownerOperatorMap = new Map<string, ContactInfo>();
    for (const member of readyMembers) {
      const contacts = member.facility?.contacts ?? [];
      for (const contact of contacts) {
        if (
          contact.reportingRoles?.includes(
            ContactReportRole.EpcraOwnerOperator
          ) &&
          contact.person
        ) {
          const person = contact.person;
          const name = [person.first, person.last].filter(Boolean).join(" ");
          const key = `${name}-${person.email ?? ""}`;
          if (!ownerOperatorMap.has(key) && name) {
            // Get phone number if available
            const phone = person.phones?.[0]?.number ?? null;
            ownerOperatorMap.set(key, {
              name,
              email: person.email,
              phone,
              title: person.title,
            });
          }
        }
      }
    }
    const uniqueOwnerOperators = Array.from(ownerOperatorMap.values());

    // Unique NAICS codes (READY members only)
    const naicsCodes = [
      ...new Set(
        readyMembers
          .map((m) => m.facility?.naicsCode)
          .filter((code): code is number => code != null)
      ),
    ].sort((a, b) => a - b);

    return {
      readyCount,
      totalMembers: members.length,
      totalChemicals,
      ehsChemicals,
      uniqueVerifiers,
      uniqueOwnerOperators,
      naicsCodes,
    };
  }, [cohort.members, cohort.chemicalCounts]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Cohort Summary
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryMetric
            label="Total Facilities"
            value={summaryData.readyCount}
          />
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryMetric
            label="Total Chemicals"
            value={summaryData.totalChemicals}
          />
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryMetric
            label="EHS Chemicals"
            value={summaryData.ehsChemicals}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 8, md: 4 }}>
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.5, fontWeight: 500 }}
            >
              NAICS Codes
            </Typography>
            {summaryData.naicsCodes.length > 0 ? (
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
              >
                {summaryData.naicsCodes
                  .slice(0, MAX_INLINE_NAICS)
                  .map((code) => (
                    <CopyableTypography
                      key={code}
                      text={String(code)}
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  ))}
                {summaryData.naicsCodes.length > MAX_INLINE_NAICS && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setNaicsModalOpen(true)}
                    sx={{ cursor: "pointer" }}
                  >
                    View all {summaryData.naicsCodes.length}
                  </Link>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Verifiers and Owner/Operators Section */}
      {(summaryData.uniqueVerifiers.length > 0 ||
        summaryData.uniqueOwnerOperators.length > 0) && (
        <>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={3}>
            {/* Verifiers */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Unique Verifiers ({summaryData.uniqueVerifiers.length})
              </Typography>
              {summaryData.uniqueVerifiers.length > 0 ? (
                <Box>
                  <ContactCard contact={summaryData.uniqueVerifiers[0]} />
                  {summaryData.uniqueVerifiers.length > 1 && (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setVerifiersModalOpen(true)}
                      sx={{ cursor: "pointer" }}
                    >
                      View all {summaryData.uniqueVerifiers.length} verifiers
                    </Link>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              )}
            </Grid>

            {/* Owner/Operators */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Owner/Operators ({summaryData.uniqueOwnerOperators.length})
              </Typography>
              {summaryData.uniqueOwnerOperators.length > 0 ? (
                <Box>
                  <ContactCard contact={summaryData.uniqueOwnerOperators[0]} />
                  {summaryData.uniqueOwnerOperators.length > 1 && (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setOwnerOperatorsModalOpen(true)}
                      sx={{ cursor: "pointer" }}
                    >
                      View all {summaryData.uniqueOwnerOperators.length}{" "}
                      owner/operators
                    </Link>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              )}
            </Grid>
          </Grid>
        </>
      )}

      {/* State-specific summary section */}
      <StateSummary cohort={cohort} />

      <CohortStateFields cohort={cohort} />

      {/* Modals */}
      <UniqueContactsModal
        open={verifiersModalOpen}
        onClose={() => setVerifiersModalOpen(false)}
        title={`Unique Verifiers (${summaryData.uniqueVerifiers.length})`}
        contacts={summaryData.uniqueVerifiers}
      />
      <UniqueContactsModal
        open={ownerOperatorsModalOpen}
        onClose={() => setOwnerOperatorsModalOpen(false)}
        title={`Owner/Operators (${summaryData.uniqueOwnerOperators.length})`}
        contacts={summaryData.uniqueOwnerOperators}
      />
      <NaicsCodesModal
        open={naicsModalOpen}
        onClose={() => setNaicsModalOpen(false)}
        codes={summaryData.naicsCodes}
      />
    </Paper>
  );
}
